/**
 * Centralized analytics tracking for admin operations
 * Standardizes event format and payload structure
 */

interface TrackingPayload {
  page?: string;
  role?: string;
  entityId?: string | number;
  latency_ms?: number;
  reducedMotion?: boolean;
  [key: string]: any;
}

// System Health specific event types
export interface SystemHealthEvents {
  system_health_loaded: {
    status: string;
    updatedAt: string | null;
    dataQuality: number | null;
    webhookSecurity: number | null;
    activeAlerts: number | null;
  };
  system_health_load_error: {
    error: string;
    retryCount: number;
  };
  system_health_refresh: {
    source: 'dashboard' | 'admin' | 'rightdock';
  };
  health_widget_toggle: {
    open: boolean;
  };
  health_view_details_click: Record<string, never>;
}

// Global Search specific event types
export interface SearchEvents {
  search_query: { 
    q: string; 
    length: number; 
    debounced: boolean; 
    requestId: string;
  };
  search_result_select: { 
    entity: 'customer'|'transaction'|'route'|'webhook'; 
    id: string; 
    position: number; 
    requestId: string;
  };
  search_error: { 
    issues?: number; 
    message?: string; 
    requestId: string;
  };
  search_empty: { 
    q: string; 
    requestId: string;
  };
  search_open: {
    scope: string;
  };
  search_close: {
    reason: string;
  };
}

/**
 * Sanitize payload to remove PII
 */
function sanitizePayload(payload: TrackingPayload): TrackingPayload {
  const sanitized = { ...payload };
  
  // Mask email-like IDs
  if (sanitized.id && typeof sanitized.id === 'string' && sanitized.id.includes('@')) {
    sanitized.id = sanitized.id.replace(/(.{2})[^@]*(@.*)/, '$1***$2');
  }
  
  return sanitized;
}

/**
 * Get current tracking context with all base information
 */
function getBaseContext() {
  const page = typeof window !== 'undefined' 
    ? (window.location.hash || '/admin').replace('#', '') 
    : 'server';
  
  const reducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    : false;

  return {
    page,
    reducedMotion,
    timestamp: Date.now(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
  };
}

/**
 * Centralized tracking function with standardized payload format
 */
export function track(event: string, payload: TrackingPayload = {}) {
  if (typeof window === 'undefined') return;

  const context = getBaseContext();
  const enrichedPayload = {
    ...context,
    ...sanitizePayload(payload),
    // Ensure page is always set from context if not provided
    page: payload.page || context.page,
  };

  // Send to gtag if available
  if ((window as any)?.gtag) {
    (window as any).gtag('event', event, enrichedPayload);
  }

  // Also log to console in dev mode for debugging
  if (import.meta.env.DEV) {
    console.info(`[TRACK] ${event}`, enrichedPayload);
  }
}

/**
 * Track timed operations with automatic duration calculation
 */
export async function trackTimed<T>(
  event: string,
  operation: () => Promise<T>,
  payload: TrackingPayload = {}
): Promise<T> {
  const startTime = performance.now();
  
  try {
    const result = await operation();
    const latency_ms = Math.round(performance.now() - startTime);
    
    track(`${event}_success`, {
      ...payload,
      latency_ms,
    });
    
    return result;
  } catch (error) {
    const latency_ms = Math.round(performance.now() - startTime);
    
    track(`${event}_error`, {
      ...payload,
      latency_ms,
      error_message: error instanceof Error ? error.message : String(error),
    });
    
    throw error;
  }
}

/**
 * React hook for component-level tracking with automatic page context
 */
export function useTracking(componentPage?: string) {
  const contextualTrack = (event: string, payload: TrackingPayload = {}) => {
    track(event, {
      ...payload,
      page: componentPage || payload.page,
    });
  };

  const contextualTrackTimed = <T>(
    event: string,
    operation: () => Promise<T>,
    payload: TrackingPayload = {}
  ) => {
    return trackTimed(event, operation, {
      ...payload,
      page: componentPage || payload.page,
    });
  };

  return {
    track: contextualTrack,
    trackTimed: contextualTrackTimed,
  };
}